<template>
  <footer class="footer-modern">
    <!-- Main Footer Content -->
    <div class="footer-main">
      <div class="container mx-auto px-6 py-12">
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-12">
          
          <!-- Brand Section -->
          <div class="footer-brand lg:col-span-1">
            <img :src="logoPath" alt="Insurance Express" class="footer-brand-logo" />
            <p class="footer-brand-description">
              Your trusted insurance partner providing comprehensive coverage and exceptional service since 1999.
            </p>
            <!-- Trust Badges -->
            <div class="mb-6 space-y-2">
              <div class="trust-badge">
                <Icon icon="mdi:shield-check" width="16" height="16" />
                <span>Licensed & Bonded</span>
              </div>
              <div class="trust-badge">
                <Icon icon="mdi:security" width="16" height="16" />
                <span>Secure & Protected</span>
              </div>
              <div class="trust-badge">
                <Icon icon="mdi:office-building-cog" width="16" height="16" />
                <span>Multiple Carrier Options</span>
              </div>
              <div class="trust-badge">
                <Icon icon="mdi:map-outline" width="16" height="16" />
                <span>Nationwide Coverage</span>
              </div>
            </div>
          </div>

          <!-- Insurance Products -->
          <div class="footer-section">
            <h3 class="footer-heading">Insurance Products</h3>
            <ul class="footer-links">
              <li><a href="/products/autoinsurance" class="footer-link">Auto Insurance</a></li>
              <li><a href="/products/homeinsurance" class="footer-link">Home Insurance</a></li>
              <li><a href="/products/lifeinsurance" class="footer-link">Life Insurance</a></li>
              <li><a href="/products/businessinsurance" class="footer-link">Business Insurance</a></li>
              <li><a href="/products/floodinsurance" class="footer-link">Flood Insurance</a></li>
              <li><a href="/products/umbrellainsurance" class="footer-link">Umbrella Insurance</a></li>
              <li><a href="/products/medicare" class="footer-link">Medicare</a></li>
            </ul>
          </div>

          <!-- Customer Support -->
          <div class="footer-section">
            <h3 class="footer-heading">Customer Support</h3>
            <ul class="footer-links">
              <li><a href="/customerservice/carrier-directory/make-claim" class="footer-link">File a Claim</a></li>
              <li><a href="/customerservice/carrier-directory/make-payment" class="footer-link">Make a Payment</a></li>
              <li><a href="/policy-search" class="footer-link">Policy Search</a></li>
              <li><a href="/customerservice" class="footer-link">Customer Service</a></li>
              <li><a href="/aboutus" class="footer-link">About Us</a></li>
              <li><a @click="showMemberCompanies = true" class="footer-link cursor-pointer">Member Companies</a></li>
            </ul>
          </div>

          <!-- Contact Information -->
          <div class="footer-section">
            <h3 class="footer-heading">Get In Touch</h3>
            <div class="space-y-4">
              <div class="contact-item">
                <Icon icon="mdi:phone" class="contact-icon" />
                <div>
                  <a href="tel:800-268-9137" class="footer-link text-lg font-semibold">800-268-9137</a>
                  <p class="text-gray-600 text-xs mt-1">Mon-Fri 8AM-6PM EST</p>
                  <p class="text-gray-600 text-xs mt-1">Text START to 800-268-9137 to receive SMS updates. Msg &amp; data rates may apply. Reply STOP to cancel.</p>
                </div>
              </div>
              <div class="contact-item">
                <Icon icon="mdi:email" class="contact-icon" />
                <div>
                  <a href="mailto:customerservice@insuranceexpress.com" class="footer-link break-all">customerservice@insuranceexpress.com</a>
                </div>
              </div>
              <div class="contact-item">
                <Icon icon="mdi:map-marker" class="contact-icon" />
                <div>
                  <a href="https://maps.google.com/?q=2005+Vista+Parkway+Suite+200,+West+Palm+Beach,+FL+33411" target="_blank" rel="noopener noreferrer" class="footer-link">
                    2005 Vista Parkway Suite 200<br>West Palm Beach, FL 33411
                  </a>
                </div>
              </div>
            </div>
            
            <!-- Quick Quote CTA -->
            <div class="mt-6">
              <a href="/chooseproducts" class="cta-button">
                <Icon icon="mdi:calculator" class="mr-2" width="18" height="18" />
                Get Quick Quote
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Bar -->
    <div class="footer-bottom">
      <div class="container mx-auto px-6 py-6">
        <div class="flex flex-col md:flex-row md:justify-between md:items-center space-y-4 md:space-y-0">
          <p class="text-white text-sm">
            © {{ new Date().getFullYear() }} Insurance Express and Member Companies. All rights reserved.
          </p>
          <div class="flex flex-wrap gap-6 text-sm">
            <a href="/legal/privacy-policy" class="footer-bottom-link">Privacy Policy</a>
            <a href="/legal/terms-of-use" class="footer-bottom-link">Terms of Use</a>
            <a href="/sitemap" class="footer-bottom-link">Sitemap</a>
            <a href="https://www.indeed.com/cmp/Insurance-Express-&-Member-Companies" target="_blank" rel="noopener noreferrer" class="footer-bottom-link">Careers</a>
          </div>
        </div>
      </div>
    </div>

    <!-- Member Companies Modal -->
    <transition name="modal">
      <div v-if="showMemberCompanies" class="modal-overlay" @click="showMemberCompanies = false">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="showMemberCompanies = false">✕</button>
          <h2 class="text-2xl font-bold mb-6">Our Member Companies</h2>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <a v-for="company in memberCompanies" :key="company.name" 
               :href="company.link" 
               target="_blank" 
               rel="noopener noreferrer"
               class="member-company-card">
              <h3 class="font-semibold text-gray-900 mb-1">{{ company.name }}</h3>
              <p class="text-sm text-gray-600">{{ company.description }}</p>
              <Icon icon="mdi:external-link" class="text-blue-500 mt-2" width="16" height="16" />
            </a>
          </div>
        </div>
      </div>
    </transition>
  </footer>
</template>

<style scoped>
/* Modern Footer Styles */
.footer-modern {
  margin-top: 5rem;
}

.footer-main {
  background-color: var(--color-bg-subtle);
  border-top: 1px solid var(--color-border);
  position: relative;
}

.footer-section {
  position: relative;
}

.footer-heading {
  color: var(--color-text-dark);
  font-size: 1.125rem;
  font-weight: var(--font-bold);
  margin-bottom: 1.5rem;
  letter-spacing: -0.025em;
  font-family: var(--font-sans);
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li {
  margin-bottom: 0.75rem;
}

.footer-link {
  color: var(--color-text-light);
  font-size: 0.875rem;
  text-decoration: none;
  transition: color 0.2s ease;
  line-height: 1.5;
  font-family: var(--font-sans);
}

.footer-link:hover {
  color: var(--color-primary);
}

.contact-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.contact-icon {
  color: var(--color-primary);
  flex-shrink: 0;
  margin-top: 0.125rem;
  width: 20px;
  height: 20px;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  background-color: var(--color-primary);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: var(--font-medium);
  text-decoration: none;
  transition: all 0.2s ease;
  font-family: var(--font-sans);
  border: none;
  cursor: pointer;
}

.cta-button:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-1px);
}

.footer-brand img {
  height: 3rem;
  margin-bottom: 1.5rem;
}

.footer-brand p {
  color: var(--color-text-light);
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  max-width: 20rem;
  font-family: var(--font-sans);
}

.footer-brand-logo {
  height: 3rem;
  margin-bottom: 1.5rem;
}

.footer-brand-description {
  color: var(--color-text-light);
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  max-width: 20rem;
  font-family: var(--font-sans);
}

.trust-badge {
  display: flex;
  align-items: center;
  background-color: white;
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  color: var(--color-text-dark);
  font-weight: var(--font-medium);
}

.trust-badge svg {
  color: #10B981;
  margin-right: 0.5rem;
}

.footer-bottom {
  background-color: var(--color-text-light);
  border-top: 1px solid var(--color-border-strong);
}

.footer-bottom-link {
  color: #E5E7EB;
  text-decoration: none;
  transition: color 0.2s ease;
  font-family: var(--font-sans);
}

.footer-bottom-link:hover {
  color: white;
}

/* Member Companies Modal Styles */
.member-company-card {
  background: white;
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  padding: 1rem;
  text-decoration: none;
  transition: all 0.2s ease;
  display: block;
}

.member-company-card:hover {
  border-color: var(--color-primary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal-content {
  background-color: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 800px;
  position: relative;
  text-align: left;
  max-height: 90vh;
  overflow-y: auto;
  padding: 2rem;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background-color: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background-color: #f3f4f6;
  color: #374151;
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.9) translateY(20px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .footer-main {
    padding-top: 3rem;
    padding-bottom: 3rem;
  }
  
  .footer-heading {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
  
  .contact-item {
    flex-direction: row;
    align-items: flex-start;
  }
  
  .cta-button {
    width: 100%;
    justify-content: center;
  }
  
  .modal-content {
    margin: 1rem;
    padding: 1.5rem;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .footer-link,
  .cta-button,
  .member-company-card {
    transition: none;
  }
  
  .cta-button:hover,
  .member-company-card:hover {
    transform: none;
  }
}
</style>

<script>
import { Icon } from '@iconify/vue';
import logoPath from '@/assets/images/insurance_express-main-logo.svg';

export default {
  name: 'AppFooter',
  components: {
    Icon
  },
  data() {
    return {
      logoPath,
      showPrivacyPolicy: false,
      showTermsOfService: false,
      showMemberCompanies: false,
      memberCompanies: [
        { name: 'Rocket Flood', link: 'https://rocketflood.com/', description: 'Flood insurance specialists' },
        { name: 'Rocket MGA', link: 'https://rocketmga.com/', description: 'Managing general agency services' },
        { name: 'Str8line Group', link: 'http://www.str8linegroup.com/', description: 'Insurance solutions provider' },
        { name: 'QuoteSlash', link: 'https://quoteslash.com/', description: 'Digital insurance platform' },
        { name: 'Protego VIP', link: 'https://protegovip.com/', description: 'VIP protection services' },
        { name: 'TheLeadExchange', link: 'https://theleadexchange.com/', description: 'Lead generation platform' },
        { name: 'NuBuild', link: 'https://nubuildinsurance.com/', description: 'Construction insurance' },
        { name: 'The IE Pipeline', link: 'https://iepipeline.com/', description: 'Insurance pipeline solutions' },
        { name: 'Graham Newton & McMahon Inc.', link: 'https://gnminsurance.com/', description: 'Professional liability insurance' },
        { name: 'Noble Care', link: 'https://www.noblecareinsurance.com/', description: 'Healthcare insurance solutions' }
      ]
    };
  }
};
</script>
